/**
 * src/lib/voice/streamingStt.js — T2: client PCM16 mic -> WS streamer for streaming STT
 *
 * Spec: docs/plans/2026-06-06-streaming-stt-endpoint-spec.md §6 T2.
 *
 * RECONCILED to the proxy's REAL protocol (services/stt-proxy/*) — CEO decision
 * PCM16 v1 (opus later). The earlier T2 build sent WebM-opus; OpenAI
 * gpt-realtime-whisper accepts ONLY raw PCM16 24kHz mono LE, and the proxy's
 * default `pcm16` wire format does a ZERO-decode passthrough (audio.js
 * `pcm16ToBase64`). So the client must send raw PCM16 — and the iOS-Safari
 * reliable way to get raw PCM samples in the browser is an AudioWorklet (NOT
 * MediaRecorder, which only emits container/opus output the pcm16 path rejects).
 *
 * STANDALONE module — the conversation-seam wiring is T3 (do NOT import this
 * from useWhisperRecording yet). Behind VITE_STREAMING_STT (default OFF); the
 * flag is read by the seam (T3), not here, so this module stays pure/testable.
 *
 * What it does (browser side of the streaming-STT WebSocket) — matches the proxy
 * contract in services/stt-proxy/session.js verbatim:
 *   1. Acquires the mic (getUserMedia) and captures audio through an
 *      **AudioWorklet** (src/lib/voice/pcm16-worklet.js) that downsamples to
 *      24kHz mono and converts Float32 -> Int16 LE on the audio thread.
 *   2. Opens ONE WebSocket to the proxy (wss://memory.acnautitech.com/stt-stream),
 *      authed with the app JWT from getAuthHeaders(). Browsers cannot set
 *      arbitrary headers on `new WebSocket()`, so the bearer token rides the
 *      Sec-WebSocket-Protocol header as `bearer.<jwt>` (alongside the stable
 *      `acn-stt` marker) — kept out of the URL/access logs (WP-15). The proxy's
 *      auth.js extractToken() reads the subprotocol header; server.js echoes
 *      acn-stt back so browsers don't reject the 101.
 *   3. On open, sends the proxy's control frame {type:'start', format:'pcm16',
 *      language} so the proxy pins the wire format + language hint.
 *   4. Forwards each worklet PCM16 ArrayBuffer as a RAW BINARY WS frame.
 *   5. On stop()/speech-end, sends {type:'commit'} — gpt-realtime-whisper has NO
 *      server VAD, so the proxy manual-commits ONLY when the client says speech
 *      ended; without this the final transcript is never produced. Then closes
 *      with {type:'stop'} + a clean 1000 close.
 *   6. Parses {type:'partial'|'final'|'error'|'session'|'cost'} JSON frames into
 *      the onPartial/onFinal/onError/onOpen/onClose callbacks (delta->onPartial,
 *      final->onFinal).
 *   7. Reconnect-with-replay on a transient (non-1000) drop, up to maxReconnects.
 *   8. Enforces a hard max-session duration cap.
 *
 * iOS Safari caveat: an AudioContext starts `suspended` and must be `resume()`d
 * inside a user gesture; iOS also frequently forces the context to 48kHz
 * regardless of the requested sampleRate. The worklet therefore always
 * downsamples from the ACTUAL ctx.sampleRate to 24kHz, and start() resumes the
 * context (start() is invoked from a user-gesture handler by the T3 seam).
 *
 * Observability: emits through the EXISTING voiceLogger (logVoiceEvent) — does
 * NOT fork a new logger. Stage step names follow spec §4:
 *   stt_stream.connect | stt_stream.first_partial | stt_stream.final | stt_stream.fallback
 * threaded with the turn trace_id + user_id passed in by the caller (T3).
 */

import { getAuthHeaders } from '../api/client';
import { logVoiceEvent } from './voiceLogger';
import { registerMic, releaseMic } from './micRegistry';

// The AudioWorklet processor module. Resolved via `new URL(..., import.meta.url)`
// so Vite emits it as a standalone asset and `audioWorklet.addModule()` can load
// it as a real module URL in dev + production builds (the worklet runs on the
// audio render thread, NOT the main bundle).
const PCM16_WORKLET_URL = new URL('./pcm16-worklet.js', import.meta.url);
const PCM16_PROCESSOR_NAME = 'pcm16-downsample';
const TARGET_SAMPLE_RATE = 24000;   // OpenAI realtime PCM16 contract (24kHz mono LE)

const DEFAULT_MAX_SESSION_MS = 10 * 60 * 1000; // 10-min cap (spec §1)
// TICKET-VOICE-FINAL-DEADLOCK — how long stop() holds the socket open after
// commit so the proxy can still deliver the {type:'final'} frame. The final
// arrives OVER this socket; closing in the same tick as commit guaranteed it
// was never delivered (every turn fell back to UVTP and died). Mic + audio
// graph are released immediately regardless — only the WS lingers.
const FINAL_GRACE_MS = 5_000;
const DEFAULT_MAX_RECONNECTS = 2;   // spec §1: two reconnect attempts, then fallback
const DEFAULT_RECONNECT_DELAY_MS = 400;
const REPLAY_BUFFER_MAX = 48;       // recent PCM16 chunks replayed on reconnect

/**
 * Build the subprotocol list for the WebSocket handshake.
 * The JWT rides `Sec-WebSocket-Protocol: bearer.<jwt>, acn-stt` so it is
 * never written to the URL or access logs (WP-15). The proxy's auth.js
 * extractToken() reads the subprotocol header; server.js echoes `acn-stt`
 * back (or the bearer.* value when acn-stt is absent) to satisfy browsers
 * that require the server to select at least one offered subprotocol.
 * Fallback: if no token is available, connect without a subprotocol and let
 * the proxy 401 (cleaner than embedding a null in the URL).
 */
function buildWsProtocols(token) {
  if (!token) return undefined;
  // Offer both: acn-stt (stable marker the proxy always echoes) +
  // bearer.<jwt> (credential carrier, never echoed back by the proxy).
  return [`bearer.${token}`, 'acn-stt'];
}

/**
 * Create a streaming-STT client session.
 *
 * @param {object} opts
 * @param {string} opts.proxyUrl              wss://<box>/stt-stream
 * @param {(text:string)=>void} [opts.onPartial]  interim transcript delta
 * @param {(text:string)=>void} [opts.onFinal]    final transcript for the turn
 * @param {(err:Error)=>void}   [opts.onError]    fatal/stream error
 * @param {()=>void}            [opts.onOpen]      socket open + ready
 * @param {(info:object)=>void} [opts.onClose]    socket closed
 * @param {string} [opts.trace_id]            turn trace id (threaded to spine)
 * @param {string} [opts.user_id]             auth uid (threaded to spine)
 * @param {string} [opts.language]            'en' | 'es' language hint
 * @param {string} [opts.model]               STT model name from the proxy allowlist
 *                                            (e.g. 'gpt-4o-transcribe'); omit to use
 *                                            the proxy's env default.
 * @param {number} [opts.maxSessionMs]
 * @param {number} [opts.maxReconnects]
 * @param {number} [opts.reconnectDelayMs]
 * @returns {{ start():Promise<void>, stop():void, commit():void, isActive():boolean }}
 */
export function createStreamingStt(opts = {}) {
  const {
    proxyUrl,
    onPartial,
    onFinal,
    onError,
    onOpen,
    onClose,
    trace_id = null,
    user_id = null,
    language = null,
    model = null,
    maxSessionMs = DEFAULT_MAX_SESSION_MS,
    maxReconnects = DEFAULT_MAX_RECONNECTS,
    reconnectDelayMs = DEFAULT_RECONNECT_DELAY_MS,
  } = opts;

  if (!proxyUrl) throw new Error('createStreamingStt: proxyUrl is required');

  const baseMeta = { trace_id, user_id, source: 'client' };

  let ws = null;
  let stream = null;
  let token = null;
  // ── Audio graph (PCM16 capture) ──
  let audioCtx = null;         // AudioContext
  let sourceNode = null;       // MediaStreamAudioSourceNode
  let workletNode = null;      // AudioWorkletNode running pcm16-worklet.js
  let active = false;          // user-intent: between start() and stop()
  let stopped = false;         // hard-stopped (no reconnect)
  let started = false;         // proxy {type:'start'} sent for the current socket
  let firstPartialSeen = false;
  let connectStartedAt = 0;
  let reconnectCount = 0;
  let sessionTimer = null;
  let reconnectTimer = null;
  let finalGraceTimer = null;  // TICKET-VOICE-FINAL-DEADLOCK — deferred close after stop()
  const replayBuffer = [];     // recent PCM16 chunks, replayed on reconnect

  function emit(step, status, message, meta = {}) {
    logVoiceEvent(step, status, message, { ...baseMeta, ...meta });
  }

  function reportError(err, code) {
    const e = err instanceof Error ? err : new Error(String(err?.message ?? err ?? 'streaming_stt_error'));
    emit('stt_stream.final', 'error', e.message, { error_code: code ?? 'stream_error' });
    try {
      onError?.(e);
    } catch {
      // spine-emit-ok: consumer callback threw — already emitted above, don't mask
    }
  }

  function clearTimers() {
    if (sessionTimer) { clearTimeout(sessionTimer); sessionTimer = null; }
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  }

  function teardownAudio() {
    if (workletNode) {
      try { workletNode.port.postMessage('stop'); } catch {
        // spine-emit-ok: signalling the worklet to stop is best-effort
      }
      try { workletNode.disconnect(); } catch {
        // spine-emit-ok: disconnect on an already-torn node is benign
      }
      workletNode = null;
    }
    if (sourceNode) {
      try { sourceNode.disconnect(); } catch {
        // spine-emit-ok: disconnect on an already-torn source is benign
      }
      sourceNode = null;
    }
    if (audioCtx) {
      try { audioCtx.close?.(); } catch {
        // spine-emit-ok: AudioContext.close is best-effort cleanup
      }
      audioCtx = null;
    }
    if (stream) {
      try { releaseMic(stream); } catch {
        // spine-emit-ok: releaseMic is idempotent best-effort cleanup
      }
      stream = null;
    }
  }

  function handleMessage(ev) {
    let msg;
    try {
      msg = JSON.parse(ev.data);
    } catch {
      return; // spine-emit-ok: non-JSON / binary frame from proxy is ignored
    }
    if (!msg || typeof msg !== 'object') return;
    switch (msg.type) {
      case 'partial':
        if (!firstPartialSeen) {
          firstPartialSeen = true;
          emit('stt_stream.first_partial', 'ok', 'first interim transcript', {
            duration_ms: connectStartedAt ? Date.now() - connectStartedAt : null,
          });
        }
        if (typeof msg.text === 'string') {
          try { onPartial?.(msg.text); } catch {
            // spine-emit-ok: consumer onPartial threw — non-fatal UI callback
          }
        }
        break;
      case 'final':
        emit('stt_stream.final', 'ok', 'final transcript', {
          duration_ms: connectStartedAt ? Date.now() - connectStartedAt : null,
          text_length: typeof msg.text === 'string' ? msg.text.length : null,
        });
        if (typeof msg.text === 'string') {
          try { onFinal?.(msg.text); } catch {
            // spine-emit-ok: consumer onFinal threw — non-fatal UI callback
          }
        }
        // TICKET-VOICE-FINAL-DEADLOCK — this was the frame stop() held the
        // socket open for; the turn is complete, close it now.
        if (stopped) closeSocket();
        break;
      case 'error':
        reportError(new Error(msg.message || msg.code || 'proxy error'), msg.code || 'proxy_error');
        // No final is coming after a proxy error — release the held socket.
        if (stopped) closeSocket();
        break;
      case 'session':
        // lifecycle ack ({state:'ready'|'closing'}) — no-op beyond the connect
        // emit fired on open; the actual close is handled by ws.onclose.
        break;
      case 'cost':
        // proxy telemetry frame emitted on close ({audio_minutes, est_usd}) — no-op
        break;
      default:
        break; // unknown frame type — ignore forward-compatibly
    }
  }

  function sendControl(obj) {
    if (!ws || ws.readyState !== 1 /* OPEN */) return false;
    try {
      ws.send(JSON.stringify(obj));
      return true;
    } catch {
      // spine-emit-ok: control send on a closing socket surfaces via onclose -> reconnect
      return false;
    }
  }

  function sendPcm(buffer) {
    if (!ws || ws.readyState !== 1 /* OPEN */) return;
    try { ws.send(buffer); } catch {
      // spine-emit-ok: PCM send on a closing socket triggers onclose -> reconnect
    }
  }

  function flushReplay() {
    if (!ws || ws.readyState !== 1 /* OPEN */) return;
    for (const frame of replayBuffer) sendPcm(frame);
  }

  function attachSocket() {
    connectStartedAt = Date.now();
    started = false;
    // JWT rides Sec-WebSocket-Protocol (bearer.<jwt> + acn-stt), NOT the URL,
    // so it stays out of access logs. buildWsProtocols() returns undefined when
    // no token is set; new WebSocket(url, undefined) === new WebSocket(url).
    ws = new WebSocket(proxyUrl, buildWsProtocols(token));
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => {
      emit('stt_stream.connect', 'ok', 'ws open', {
        duration_ms: Date.now() - connectStartedAt,
        reconnect: reconnectCount > 0 ? reconnectCount : undefined,
      });
      // {type:'start'} — pin the PCM16 wire format + language + model. The proxy
      // (session.js) reads ctrl.format + ctrl.language + ctrl.model off this
      // exact frame. Omit model when unset so the proxy falls back to its env
      // default (gpt-realtime-whisper) without any override.
      started = sendControl({
        type: 'start',
        format: 'pcm16',
        language: language || undefined,
        model: model || undefined,
      });
      // TICKET-CODEX-3368864099 — flush any audio buffered while the socket was
      // still CONNECTING (sendPcm drops frames until readyState === OPEN).  On the
      // first open reconnectCount is 0 but audio may still have been pushed into
      // replayBuffer during the handshake; flush unconditionally so the first
      // word(s) of a turn are not silently dropped on slower networks.
      flushReplay();
      try { onOpen?.(); } catch {
        // spine-emit-ok: consumer onOpen threw — non-fatal
      }
    };
    ws.onmessage = handleMessage;
    ws.onerror = (err) => {
      // TICKET-CODEX-3368369484 — a socket error almost always precedes the
      // onclose event.  Do NOT call reportError / onError here: doing so
      // settles the finalPromise in conversationStreaming with streamError
      // immediately, so awaitFinal() returns an error even when the subsequent
      // onclose path reconnects successfully and the new socket produces a valid
      // final transcript.  Let onclose own the reconnect-or-reject decision;
      // we only log a spine event here so the error is still observable.
      emit('stt_stream.final', 'error', err?.message || 'ws socket error', {
        error_code: 'ws_error',
        transient: true,
      });
    };
    ws.onclose = (ev) => {
      const cleanStop = stopped || ev?.code === 1000;
      if (cleanStop) {
        try { onClose?.(ev || { code: 1000 }); } catch {
          // spine-emit-ok: consumer onClose threw — non-fatal
        }
        return;
      }
      // transient drop mid-session → reconnect-with-replay
      if (active && reconnectCount < maxReconnects) {
        reconnectCount += 1;
        emit('stt_stream.connect', 'start', 'reconnect attempt', { attempt: reconnectCount });
        reconnectTimer = setTimeout(() => {
          if (!stopped && active) attachSocket();
        }, reconnectDelayMs);
      } else {
        // out of reconnects → emit fallback signal (T3 owns the UVTP fallback)
        emit('stt_stream.fallback', 'error', 'reconnect exhausted', {
          error_code: 'reconnect_exhausted',
          attempts: reconnectCount,
        });
        reportError(new Error('streaming WS dropped and reconnect failed'), 'reconnect_exhausted');
        try { onClose?.(ev || { code: 1006 }); } catch {
          // spine-emit-ok: consumer onClose threw — non-fatal
        }
      }
    };
  }

  // AudioWorklet capture: mic -> downsample-to-24k -> Int16 LE -> binary WS frame.
  // The worklet (pcm16-worklet.js) does the heavy lifting on the audio thread and
  // posts PCM16 ArrayBuffers to the main thread; we forward each as a raw binary
  // WS frame and keep a short replay window for reconnects.
  async function startCapture() {
    // Request the target rate; iOS Safari may ignore it and force 48k — the
    // worklet downsamples from the ACTUAL ctx.sampleRate either way.
    const Ctx = (typeof AudioContext !== 'undefined' && AudioContext)
      || (typeof window !== 'undefined' && window.webkitAudioContext);
    audioCtx = new Ctx({ sampleRate: TARGET_SAMPLE_RATE });
    // iOS: context starts suspended; resume() must run inside the start() user gesture.
    try { await audioCtx.resume?.(); } catch {
      // spine-emit-ok: resume() can reject if the gesture chain is lost; surfaced below if no audio flows
    }
    await audioCtx.audioWorklet.addModule(PCM16_WORKLET_URL);

    sourceNode = audioCtx.createMediaStreamSource(stream);
    workletNode = new AudioWorkletNode(audioCtx, PCM16_PROCESSOR_NAME);

    // Register with the global mic registry (auto-release on pagehide/visibility).
    // No MediaRecorder anymore — pass null; releaseMic still stops the stream tracks.
    registerMic(stream, null, 'streamingStt');

    workletNode.port.onmessage = (ev) => {
      const buffer = ev?.data;            // transferred Int16 PCM ArrayBuffer
      if (!buffer || buffer.byteLength === 0) return;
      // keep a short replay window for reconnects
      replayBuffer.push(buffer);
      if (replayBuffer.length > REPLAY_BUFFER_MAX) replayBuffer.shift();
      sendPcm(buffer);
    };

    // Drive the worklet: mic -> worklet -> silentGain -> ctx.destination.
    // TICKET-CODEX-3368934709: browsers commonly prune Web Audio subgraphs that
    // have no path to a destination node, so the worklet's process() callback
    // may never be pulled and no PCM frames are sent.  Connect through a gain
    // node with gain=0 (completely silent — no audio plays) so the graph has a
    // destination path and the browser keeps pulling the worklet.  The gain node
    // is torn down with the rest of the audio graph in teardownAudio().
    const silentGain = audioCtx.createGain();
    silentGain.gain.value = 0;
    sourceNode.connect(workletNode);
    workletNode.connect(silentGain);
    silentGain.connect(audioCtx.destination);
  }

  async function start() {
    if (active) return;
    active = true;
    stopped = false;
    firstPartialSeen = false;
    reconnectCount = 0;
    replayBuffer.length = 0;

    // 1) auth — token from getAuthHeaders() (NOT hardcoded)
    let auth = {};
    try {
      auth = await getAuthHeaders();
    } catch (err) {
      active = false;
      reportError(err, 'auth_failed');
      throw err;
    }
    const header = auth?.Authorization || '';
    token = header.startsWith('Bearer ') ? header.slice('Bearer '.length) : (header || null);

    // 2) mic
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      active = false;
      reportError(err, 'mic_denied');
      throw err;
    }

    // 3) socket + PCM16 AudioWorklet capture
    attachSocket();
    try {
      await startCapture();
    } catch (err) {
      active = false;
      reportError(err, 'audio_capture_failed');
      teardownAudio();
      throw err;
    }

    // 4) hard session cap
    sessionTimer = setTimeout(() => {
      emit('stt_stream.final', 'skip', 'max session duration reached', { error_code: 'session_cap' });
      stop();
    }, maxSessionMs);
  }

  /**
   * End the current turn WITHOUT closing the session: tell the proxy speech
   * ended so it manual-commits and OpenAI produces the final transcript.
   * gpt-realtime-whisper has NO server VAD — this signal is REQUIRED for a final.
   * The seam (T3) calls this on client-VAD speech-end to keep the socket warm
   * for the next turn; stop() also calls it before closing.
   */
  function commit() {
    sendControl({ type: 'commit' });
  }

  /** Close the WS now (idempotent). Used by stop()'s grace path + final/error arrival. */
  function closeSocket() {
    if (finalGraceTimer) { clearTimeout(finalGraceTimer); finalGraceTimer = null; }
    if (ws) {
      try {
        if (ws.readyState === 0 /* CONNECTING */ || ws.readyState === 1 /* OPEN */) {
          ws.close(1000, 'client stop');
        }
      } catch {
        // spine-emit-ok: close on an already-closed socket is benign
      }
    }
  }

  function stop() {
    if (stopped) return;
    stopped = true;
    active = false;
    clearTimers();
    // Mic + audio graph release IMMEDIATELY — the browser mic indicator must
    // extinguish the moment the user presses stop (TICKET-CODEX-3368864100).
    teardownAudio();
    // TICKET-VOICE-FINAL-DEADLOCK — the final transcript arrives OVER this
    // socket ~0.5-2s after commit. The old code sent {type:'stop'} and closed
    // in the same tick; BOTH killed the final: the proxy's 'stop' handler
    // tears the session (and its upstream OpenAI socket) down immediately
    // (services/stt-proxy/session.js case 'stop'), and the local close
    // removed the socket the final would have arrived on. So: send ONLY the
    // commit, HOLD the socket, and let handleMessage close it when the final
    // (or a proxy error) lands — that client close triggers the proxy's
    // normal browserWs 'close' teardown. The grace timer bounds the wait.
    if (ws && ws.readyState === 1 /* OPEN */ && started) {
      commit();
      finalGraceTimer = setTimeout(closeSocket, FINAL_GRACE_MS);
    } else {
      closeSocket();
    }
  }

  function isActive() {
    return active;
  }

  return { start, stop, commit, isActive };
}

export default createStreamingStt;
